import * as React from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import posed from 'react-pose'
import { User, hasPermission, hasAnyPermission } from '../lib/interfaces/User.interface'

type SidebarProps = {
  status: 'open' | 'collapsed'
  user: User
}

type SidebarState = {
  statusTeam: 'open' | 'closed'
  statusAdmin: 'open' | 'closed'
  statusScans: 'open' | 'closed'
  statusAccidents: 'open' | 'closed'
  statusApplication: 'open' | 'closed'
}

const SidePanel = posed.div({
  open: {
    width: 240,
    delayChildren: 300,
    staggerChildren: 50,
  },
  collapsed: {
    width: 70,
    delay: 500,
    staggerChildren: 20
  }
})

const Item = posed.span({
  open: { opacity: 1, x: 0, width: '100%' },
  collapsed: { opacity: 0, x: 20, width: 0 }
})

const Collapsible = posed.ul({
  open: {
    delayChildren: 500,
    maxHeight: 500,
    opacity: 1,
    staggerChildren: 20,
  },
  closed: {
    maxHeight: 0,
    opacity: 0,
    staggerChildren: 20,
  }
})

class Sidebar extends React.PureComponent<SidebarProps, SidebarState> {

  state: SidebarState = {
    statusTeam: 'open',
    statusAdmin: 'open',
    statusScans: 'open',
    statusAccidents: 'open',
    statusApplication: 'open' 
  }

  handleCollapseTeam = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    this.setState((state: SidebarState) => ({
      statusTeam: (state.statusTeam !== 'open') ? 'open' : 'closed'
    }))
  }

  handleCollapseApplication = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    this.setState((state: SidebarState) => ({
      statusApplication: (state.statusApplication !== 'open') ? 'open' : 'closed'
    }))
  }

  handleCollapseAccident = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    this.setState((state: SidebarState) => ({
      statusAccidents: (state.statusAccidents !== 'open') ? 'open' : 'closed'
    }))
  }

  handleCollapseScans = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    this.setState((state: SidebarState) => ({
      statusScans: (state.statusScans !== 'open') ? 'open' : 'closed'
    }))
  }

  handleCollapseAdmin = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    this.setState((state: SidebarState) => ({
      statusAdmin: (state.statusScans !== 'open') ? 'open' : 'closed'
    }))
  }

  render() {
    const { status, user } = this.props
    const { statusTeam, statusAdmin, statusScans, statusApplication, statusAccidents } = this.state

    return (
      <SidePanel pose={status} className={classnames('ggmenu', {
        ggmenu__open: (status === 'open'),
        ggmenu__collapsed: (status === 'collapsed'),
      })}>
        <Link to="/dashboard" className="ggmenu-item ggmenu-item__title ggmenu-item__yellow">
          <div className={classnames('gg-logo', {
            'gg-logo__open': (status === 'open'),
            'gg-logo__collapsed': (status === 'collapsed')
          })} />
        </Link>

        <div className="flex-grow-1 min-height-0" style={{ overflowY: 'auto' }}>
          {hasAnyPermission(user, ['ROLE_CITY_MANAGER', 'ROLE_EMPLOYEE_DIRECTORY', 'ROLE_OFFICE_TERMINATE']) &&
            <div className="ggmenu-collapsible">
              <a href="" className="ggmenu-item ggmenu-item__title ggmenu-item__darkgrey" onClick={this.handleCollapseTeam}>
                <FontAwesomeIcon icon={["far", "user"]} />
                <Item>
                  Team Details
                  <FontAwesomeIcon
                    className="ggmenu-collapsible-toggleicon"
                    icon={["fas", "chevron-down"]}
                    flip={(statusTeam === 'open') ? 'vertical' : undefined} />
                </Item>
              </a>

              <Collapsible pose={statusTeam} className="ggmenu-collapsible-list list-unstyled">
                <li>
                  <Link to="/employees" className="ggmenu-item">
                    <FontAwesomeIcon icon={['fas', 'user-friends']} />
                    <Item>All Employees</Item>
                  </Link>
                </li>

                {hasPermission(user, 'ROLE_OFFICE_TERMINATE') &&
                  <li>
                    <Link to="/terminations" className="ggmenu-item">
                      <FontAwesomeIcon icon={["fas", "user-slash"]} />
                      <Item>Terminations</Item>
                    </Link>
                  </li>
                }
              </Collapsible>
            </div>
          }

          {hasAnyPermission(user, ['ROLE_CITY_MANAGER', 'ROLE_APPLICATION_ADMIN', 'ROLE_JOB_APPLICATION']) &&
            <div className="ggmenu-collapsible">
              <a href="" className="ggmenu-item ggmenu-item__title ggmenu-item__darkgrey" onClick={this.handleCollapseApplication}>
                <FontAwesomeIcon icon={["far", "user"]} />
                <Item>
                  Applications
                  <FontAwesomeIcon
                    className="ggmenu-collapsible-toggleicon"
                    icon={["fas", "chevron-down"]}
                    flip={(statusApplication === 'open') ? 'vertical' : undefined} />
                </Item>
              </a>

              <Collapsible pose={statusApplication} className="ggmenu-collapsible-list list-unstyled">
                {hasAnyPermission(user, ['ROLE_APPLICATION_ADMIN', 'ROLE_JOB_APPLICATION']) &&
                  <li>
                    <Link to="/applications" className="ggmenu-item">
                      <FontAwesomeIcon icon={['far', 'list-alt']} />
                      <Item>Applications</Item>
                    </Link>
                  </li>
                }

                {hasPermission(user, 'ROLE_CITY_MANAGER') &&
                  <li>
                    <Link to="/applications/positions" className="ggmenu-item">
                      <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                      <Item>Job Positions</Item>
                    </Link>
                  </li>
                }
              </Collapsible>
            </div>
          }

          {hasAnyPermission(user, ['ROLE_ACCIDENT_ADMIN', 'ROLE_CITY_MANAGER']) &&
            <div className="ggmenu-collapsible">
              <a href="" className="ggmenu-item ggmenu-item__title ggmenu-item__darkgrey" onClick={this.handleCollapseAccident}>
                <div className={'car-crash'} />
                <Item>
                  Accidents
                  <FontAwesomeIcon
                    className="ggmenu-collapsible-toggleicon"
                    icon={["fas", "chevron-down"]}
                    flip={(statusAccidents === 'open') ? 'vertical' : undefined} />
                </Item>
              </a>

              <Collapsible pose={statusAccidents} className="ggmenu-collapsible-list list-unstyled">
                <li>
                  <Link to="/accidents" className="ggmenu-item">
                    <FontAwesomeIcon icon={["fas", "chart-line"]} />
                    <Item>Vehicle Accidents</Item>
                  </Link>
                </li>

                {/* <li>
                  <Link to="/accidents/report" className="ggmenu-item">
                    <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />
                    <Item>Report New Accident</Item>
                  </Link>
                </li>*/}

                {hasPermission(user, 'ROLE_ADMIN') &&
                  <li>
                    <Link to="/invoices" className="ggmenu-item">
                      <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />
                      <Item>Invoices</Item>
                    </Link>
                  </li>
                }

                {/*<li>
                  <Link to="/accidents/reports" className="ggmenu-item">
                    <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />
                    <Item>Reports</Item>
                  </Link>
                </li>*/}
              </Collapsible>
            </div>
          }

          {hasAnyPermission(user, ['ROLE_SCAN_MANAGER']) &&
            <div className="ggmenu-collapsible">
              <a href="" className="ggmenu-item ggmenu-item__title ggmenu-item__darkgrey" onClick={this.handleCollapseScans}>
                <FontAwesomeIcon icon={["fas", "barcode"]} />
                <Item>
                  Scans
                  <FontAwesomeIcon
                    icon={['fas', 'chevron-down']}
                    className="ggmenu-collapsible-toggleicon"
                    flip={(statusScans === 'open') ? 'vertical' : undefined} />
                </Item>
              </a>
              <Collapsible pose={statusAccidents} className="ggmenu-collapsible-list list-unstyled">
                <li>
                  <Link to="/scan" className="ggmenu-item">
                    <FontAwesomeIcon icon={['fas', 'chart-line']} />
                    <Item>Report</Item>
                  </Link>
                </li>
                <li>
                  <Link to="/scan/employee" className="ggmenu-item">
                    <FontAwesomeIcon icon={['fas', 'chart-line']} />
                    <Item>By Employee Report</Item>
                  </Link>
                </li>
                <li>
                  <Link to="/scan/login" className="ggmenu-item">
                    <FontAwesomeIcon icon={["fas", "mobile-alt"]} />
                    <Item>Scan Login</Item>
                  </Link>
                </li>

                {hasAnyPermission(user, ['ROLE_QA_MANAGER']) && (
                  <li>
                    <Link to="/qa-check" className="ggmenu-item">
                      <FontAwesomeIcon icon={["fas", "camera"]} />
                      <Item>QA</Item>
                    </Link>
                  </li>
                )}
              </Collapsible>
            </div>
          }

          {hasAnyPermission(user, ['ROLE_ADMIN']) &&
            <div className="ggmenu-collapsible">
              <a href="" className="ggmenu-item ggmenu-item__title ggmenu-item__darkgrey" onClick={this.handleCollapseAdmin}>
                <FontAwesomeIcon icon={['fas', 'cog']} />
                <Item>
                  Admin
                  <FontAwesomeIcon
                    className="ggmenu-collapsible-toggleicon"
                    icon={["fas", "chevron-down"]}
                    flip={(statusScans === 'open') ? 'vertical' : undefined} />
                </Item>
              </a>
              <Collapsible pose={statusAdmin} className="ggmenu-collapsible-list list-unstyled">
                <li>
                  <Link to="/admin/users" className="ggmenu-item">
                    <FontAwesomeIcon icon={['fas', 'user-friends']} />
                    <Item>Users Management</Item>
                  </Link>
                </li>
              </Collapsible>
            </div>
          }
        </div>
      </SidePanel>
    )
  }
}

export default Sidebar



// WEBPACK FOOTER //
// ./src/routes/Sidebar.component.tsx